// WS-клиент с автопереподключением: state.update / health.update / errors.

import type { ClientMessage, ServerMessage } from '@besedka/shared';

type MessageListener = (message: ServerMessage) => void;
type StatusListener = (online: boolean) => void;

export class WsClient {
  private ws: WebSocket | null = null;
  private url: string;
  private closed = false;
  private backoffMs = 1000;
  private messageListeners = new Set<MessageListener>();
  private statusListeners = new Set<StatusListener>();

  constructor(query = '') {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.url = `${proto}://${location.host}/api/ws${query}`;
    this.connect();
  }

  private connect(): void {
    if (this.closed) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.backoffMs = 1000;
      for (const listener of this.statusListeners) listener(true);
    };
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        for (const listener of this.messageListeners) listener(message);
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      for (const listener of this.statusListeners) listener(false);
      if (this.closed) return;
      setTimeout(() => this.connect(), this.backoffMs);
      this.backoffMs = Math.min(this.backoffMs * 2, 15_000);
    };
    ws.onerror = () => ws.close();
  }

  send(message: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }

  onMessage(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
  }
}
